import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const NAV_ITEMS = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "资讯", to: "/news" },
];

export default function Header() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const isHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const transparent = isHome && !scrolled && !menuOpen;

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setKeyword("");
  };

  const isActive = (to: string) =>
    to === "/" ? pathname === "/" : pathname.startsWith(to);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        transparent
          ? "bg-transparent"
          : "border-b border-background-200 bg-background-50/95 backdrop-blur"
      }`}
    >
      <div className="mx-auto flex h-16 w-full items-center justify-between px-4 md:px-6">
        <Link
          to="/"
          className="flex items-center gap-2"
          aria-label="后入影视网首页"
        >
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-md font-heading text-lg font-extrabold ${
              transparent
                ? "bg-accent-500 text-white"
                : "bg-primary-600 text-background-50"
            }`}
          >
            后
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading text-lg font-extrabold tracking-tight ${
                transparent ? "text-white" : "text-foreground-950"
              }`}
            >
              后入影视
            </span>
            <span
              className={`mt-0.5 text-[10px] tracking-widest ${
                transparent ? "text-white/70" : "text-secondary-500"
              }`}
            >
              HOUJU MEDIA
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                isActive(item.to)
                  ? "text-accent-500"
                  : transparent
                    ? "text-white/85 hover:text-white"
                    : "text-foreground-700 hover:text-accent-600"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <form
            onSubmit={handleSearch}
            className={`flex items-center gap-2 rounded-md border px-3 py-1.5 ${
              transparent
                ? "border-white/30 bg-white/10"
                : "border-background-300 bg-background-100"
            }`}
          >
            <i
              className={`ri-search-line text-base ${
                transparent ? "text-white/70" : "text-secondary-500"
              }`}
            ></i>
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影视 / 资讯"
              className={`w-36 bg-transparent text-sm outline-none ${
                transparent
                  ? "text-white placeholder:text-white/60"
                  : "text-foreground-900 placeholder:text-secondary-400"
              }`}
            />
          </form>
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-label="打开菜单"
          className={`flex h-10 w-10 items-center justify-center rounded-md md:hidden ${
            transparent ? "text-white" : "text-foreground-900"
          }`}
        >
          <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-4 pt-3 md:hidden">
          <form
            onSubmit={handleSearch}
            className="mb-3 flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2"
          >
            <i className="ri-search-line text-base text-secondary-500"></i>
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影视 / 资讯"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-secondary-400"
            />
          </form>
          <nav className="flex flex-col">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-md px-3 py-3 text-sm font-medium ${
                  isActive(item.to)
                    ? "bg-primary-50 text-accent-600"
                    : "text-foreground-700"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}